import {
  Activity,
  AlertCircle,
  Ban,
  CalendarRange,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Coins,
  EyeOff,
  Flag,
  GripVertical,
  LayoutDashboard,
  ListChecks,
  Plus,
  Search,
  SlidersHorizontal,
  Smartphone,
  Target,
  X,
} from 'lucide-react';
import {
  Fragment,
  useMemo,
  useState,
  type ComponentType,
  type DragEvent,
  type ReactNode,
} from 'react';
import { Link } from 'react-router';

import { cn } from '../../lib/utils';
import { DappIcon } from './DappPickerDialog';
import { type AppMoveEdge } from './questWorkspaceLocalState';
import {
  appDisplayName,
  questDisplayName,
  questWorkspaceTargetKey,
  roundDisplayName,
  seasonDisplayName,
  statusDotToneClass,
  statusTone,
  type QuestWorkspaceForm,
  type QuestWorkspaceItemRef,
} from './questWorkspace';

type QuickFilter = 'all' | 'issues' | 'disabled' | 'unrevealed';

// Which half of the row the pointer is in decides whether the dragged app
// lands before or after the row's app.
function dropEdgeOf(event: DragEvent<HTMLDivElement>): AppMoveEdge {
  const rect = event.currentTarget.getBoundingClientRect();
  return event.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
}

function GroupLabel(props: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'px-2 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wider text-neutral-400',
        props.className,
      )}
    >
      {props.children}
    </div>
  );
}

function Branch(props: { children: ReactNode }) {
  return (
    <div
      role="group"
      className="ml-3 space-y-0.5 border-l border-neutral-800 pl-1.5"
    >
      {props.children}
    </div>
  );
}

function StatusDot(props: { status: string }) {
  const tone = statusTone(props.status);
  return (
    <span className="inline-flex items-center" title={props.status}>
      <span
        className={cn(
          'size-2 rounded-full ring-1 ring-black/40',
          statusDotToneClass[tone],
        )}
        aria-hidden="true"
      />
      <span className="sr-only">{props.status}</span>
    </span>
  );
}

function NavRow(props: {
  icon: ComponentType<{ className?: string }>;
  iconUrl?: string;
  label: string;
  meta?: string;
  status?: string;
  disabled?: boolean;
  unrevealed?: boolean;
  dimmed?: boolean;
  issueCount: number;
  isActive: boolean;
  level: number;
  onClick: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  trailing?: ReactNode;
}) {
  const Icon = props.icon;

  return (
    <div
      className={cn(
        'flex items-stretch rounded-md transition-colors',
        props.isActive
          ? 'bg-teal-500/10 ring-1 ring-inset ring-teal-500/25'
          : 'hover:bg-neutral-800/50',
      )}
    >
      {props.onToggleCollapse ? (
        <button
          type="button"
          tabIndex={-1}
          aria-label={`${props.isCollapsed ? 'Expand' : 'Collapse'} ${props.label}`}
          aria-expanded={!props.isCollapsed}
          onClick={props.onToggleCollapse}
          className="flex w-5 shrink-0 items-center justify-center rounded text-neutral-500 transition-colors hover:text-neutral-100"
        >
          <ChevronRight
            className={cn(
              'size-3 transition-transform',
              !props.isCollapsed && 'rotate-90',
            )}
          />
        </button>
      ) : (
        <span className="w-5 shrink-0" />
      )}
      <button
        type="button"
        aria-current={props.isActive ? 'true' : undefined}
        onClick={props.onClick}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2 rounded-md py-1.5 pr-1.5 text-left text-sm transition-colors',
          props.isActive ? 'text-white' : 'text-neutral-300 hover:text-white',
          props.dimmed && !props.isActive && 'text-neutral-500',
        )}
      >
        {props.iconUrl ? (
          <DappIcon
            url={props.iconUrl}
            name={props.label}
            className="size-4 shrink-0 rounded"
          />
        ) : (
          <Icon
            className={cn(
              'size-3.5 shrink-0',
              props.isActive ? 'text-teal-300' : 'text-neutral-500',
            )}
          />
        )}
        <span className="min-w-0 flex-1 truncate">
          {props.label}
          {props.meta && (
            <span className="ml-1.5 text-xs font-normal text-neutral-500">
              {props.meta}
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center justify-end gap-1.5">
          {props.issueCount > 0 && (
            <span
              className="inline-flex items-center gap-0.5 text-rose-400"
              title={`${props.issueCount} issue${props.issueCount === 1 ? '' : 's'}`}
            >
              <AlertCircle className="size-3" aria-hidden="true" />
              <span className="text-[10px] font-semibold tabular-nums">
                {props.issueCount}
              </span>
              <span className="sr-only">
                {props.issueCount} issue{props.issueCount === 1 ? '' : 's'}
              </span>
            </span>
          )}
          {props.status && <StatusDot status={props.status} />}
          {props.disabled && (
            <span
              className="inline-flex items-center text-rose-300"
              title="Disabled"
            >
              <Ban className="size-3.5" aria-hidden="true" />
              <span className="sr-only"> — disabled</span>
            </span>
          )}
          {props.unrevealed && (
            <span
              className="inline-flex items-center text-amber-300"
              title="Unrevealed"
            >
              <EyeOff className="size-3.5" aria-hidden="true" />
              <span className="sr-only"> — unrevealed</span>
            </span>
          )}
        </span>
      </button>
      {props.trailing}
    </div>
  );
}

function AddRow(props: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className="flex w-full items-center rounded-md py-1 pr-2 text-left text-sm text-neutral-500 transition-colors hover:bg-neutral-800/40 hover:text-teal-300"
    >
      <span className="w-5 shrink-0" />
      <Plus className="size-3.5 shrink-0 text-teal-400/70" />
      <span className="ml-2 truncate">{props.label}</span>
    </button>
  );
}

function ControlChip(props: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={props.active}
      onClick={props.onClick}
      className={cn(
        'rounded-md px-2 py-1 text-xs font-medium transition-colors',
        props.active
          ? 'bg-teal-500/15 text-teal-300 ring-1 ring-inset ring-teal-500/30'
          : 'text-neutral-400 hover:bg-neutral-800/60 hover:text-neutral-200',
      )}
    >
      {props.children}
    </button>
  );
}

export function QuestWorkspaceNav(props: {
  workspace: QuestWorkspaceForm;
  selected: QuestWorkspaceItemRef;
  issueCounts: Map<string, number>;
  descendantIssueCounts: Map<string, number>;
  collapsedKeys: ReadonlySet<string>;
  onSelect: (ref: QuestWorkspaceItemRef) => void;
  onToggleCollapse: (ref: QuestWorkspaceItemRef) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onAddSeason: () => void;
  onAddRound: (seasonId: string) => void;
  onAddApp: (roundId: string) => void;
  onAddQuest: (appId: string) => void;
  onMoveApp: (appId: string, targetAppId: string, edge: AppMoveEdge) => void;
}) {
  const { workspace } = props;
  const selectedKey = questWorkspaceTargetKey(props.selected);
  const [query, setQuery] = useState('');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draggedApp, setDraggedApp] = useState<{
    appId: string;
    roundId: string;
  } | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    appId: string;
    edge: AppMoveEdge;
  } | null>(null);

  const clearAppDrag = () => {
    setDraggedApp(null);
    setDropTarget(null);
  };

  const normalizedQuery = query.trim().toLowerCase();
  const filtering = normalizedQuery !== '' || quickFilter !== 'all';
  // Anything collapsed at all → the toggle offers "Expand all"; otherwise it
  // offers "Collapse all". One context-aware control instead of two icons.
  const allExpanded = props.collapsedKeys.size === 0;
  const quickFilterActive = quickFilter !== 'all';

  const totalIssues = useMemo(() => {
    let count = 0;
    for (const value of props.issueCounts.values()) {
      count += value;
    }
    return count;
  }, [props.issueCounts]);

  // When filtering, compute the set of nav rows to render: a node shows if it
  // directly matches or has a matching descendant, and matching nodes are
  // always expanded regardless of collapse state.
  const visibleKeys = useMemo(() => {
    if (!filtering) {
      return null;
    }

    const textMatch = (value: string) =>
      normalizedQuery === '' || value.toLowerCase().includes(normalizedQuery);

    const directMatch = (
      ref: QuestWorkspaceItemRef,
      text: string,
      disabled: boolean,
      unrevealed = false,
    ) => {
      if (!textMatch(text)) {
        return false;
      }
      if (quickFilter === 'issues') {
        return (props.issueCounts.get(questWorkspaceTargetKey(ref)) ?? 0) > 0;
      }
      if (quickFilter === 'disabled') {
        return disabled;
      }
      if (quickFilter === 'unrevealed') {
        return unrevealed;
      }
      return true;
    };

    const visible = new Set<string>();
    const reveal = (ref: QuestWorkspaceItemRef) =>
      visible.add(questWorkspaceTargetKey(ref));

    for (const season of workspace.seasons) {
      const seasonRef: QuestWorkspaceItemRef = {
        kind: 'season',
        id: season.id,
      };
      let seasonHasChild = false;
      for (const round of workspace.rounds) {
        if (round.seasonId !== season.id) {
          continue;
        }
        const roundRef: QuestWorkspaceItemRef = {
          kind: 'round',
          id: round.id,
        };
        let roundHasChild = false;
        for (const app of workspace.apps) {
          if (app.roundId !== round.id) {
            continue;
          }
          const appRef: QuestWorkspaceItemRef = { kind: 'app', id: app.id };
          let appHasChild = false;
          for (const quest of workspace.quests) {
            if (quest.roundAppId !== app.id) {
              continue;
            }
            const questRef: QuestWorkspaceItemRef = {
              kind: 'quest',
              id: quest.id,
            };
            if (
              directMatch(questRef, questDisplayName(quest, 0), quest.disabled)
            ) {
              reveal(questRef);
              appHasChild = true;
            }
          }
          if (
            appHasChild ||
            directMatch(
              appRef,
              appDisplayName(app, 0),
              app.disabled,
              app.unrevealed,
            )
          ) {
            reveal(appRef);
            roundHasChild = true;
          }
        }
        if (
          roundHasChild ||
          directMatch(roundRef, roundDisplayName(round, 0), round.disabled)
        ) {
          reveal(roundRef);
          seasonHasChild = true;
        }
      }
      if (
        seasonHasChild ||
        directMatch(seasonRef, seasonDisplayName(season, 0), false)
      ) {
        reveal(seasonRef);
      }
    }
    return visible;
  }, [filtering, normalizedQuery, quickFilter, workspace, props.issueCounts]);

  const isVisible = (ref: QuestWorkspaceItemRef) =>
    !visibleKeys || visibleKeys.has(questWorkspaceTargetKey(ref));

  const isCollapsed = (ref: QuestWorkspaceItemRef) =>
    filtering ? false : props.collapsedKeys.has(questWorkspaceTargetKey(ref));

  const issuesFor = (ref: QuestWorkspaceItemRef) => {
    const key = questWorkspaceTargetKey(ref);
    return (
      (props.issueCounts.get(key) ?? 0) +
      (props.descendantIssueCounts.get(key) ?? 0)
    );
  };

  const rowProps = (ref: QuestWorkspaceItemRef, level: number) => {
    const key = questWorkspaceTargetKey(ref);
    return {
      isActive: key === selectedKey,
      issueCount: issuesFor(ref),
      level,
      onClick: () => props.onSelect(ref),
    };
  };

  const branchProps = (ref: QuestWorkspaceItemRef, level: number) => ({
    ...rowProps(ref, level),
    isCollapsed: isCollapsed(ref),
    onToggleCollapse: () => props.onToggleCollapse(ref),
  });

  const countMeta = (parts: Array<[number, string]>) => {
    const text = parts
      .filter(([count]) => count > 0)
      .map(([count, label]) => `${count} ${label}${count === 1 ? '' : 's'}`)
      .join(' · ');
    return text || undefined;
  };

  return (
    <nav
      aria-label="Quest workspace"
      className="flex flex-col gap-2 rounded-xl border border-neutral-800/60 bg-neutral-900/30 p-2.5"
    >
      <div className="space-y-2">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-neutral-500"
            aria-hidden="true"
          />
          <input
            type="search"
            aria-label="Filter workspace"
            placeholder="Filter…"
            value={query}
            onInput={(event) => setQuery(event.currentTarget.value)}
            className="h-8 w-full rounded-lg border border-neutral-800 bg-neutral-950/40 pl-8 pr-7 text-sm text-neutral-100 placeholder:text-neutral-500 transition-colors focus:border-teal-500/70 focus:outline-none focus:ring-2 focus:ring-teal-500/15"
          />
          {query !== '' && (
            <button
              type="button"
              aria-label="Clear filter"
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-neutral-500 hover:text-neutral-200"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Filter options"
            aria-expanded={filtersOpen}
            aria-pressed={quickFilterActive}
            title="Filter options"
            onClick={() => setFiltersOpen((open) => !open)}
            className={cn(
              'relative inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors',
              filtersOpen || quickFilterActive
                ? 'bg-teal-500/15 text-teal-300 ring-1 ring-inset ring-teal-500/30'
                : 'text-neutral-400 hover:bg-neutral-800/60 hover:text-neutral-200',
            )}
          >
            <SlidersHorizontal className="size-3.5" />
            Filters
            {quickFilterActive && (
              <span className="size-1.5 rounded-full bg-teal-400" />
            )}
          </button>
          <button
            type="button"
            aria-label={allExpanded ? 'Collapse all' : 'Expand all'}
            title={allExpanded ? 'Collapse all' : 'Expand all'}
            onClick={allExpanded ? props.onCollapseAll : props.onExpandAll}
            className="ml-auto rounded p-1 text-neutral-500 transition-colors hover:bg-neutral-800/60 hover:text-neutral-200"
          >
            {allExpanded ? (
              <ChevronsDownUp className="size-3.5" />
            ) : (
              <ChevronsUpDown className="size-3.5" />
            )}
          </button>
        </div>
        {(filtersOpen || quickFilterActive) && (
          <div className="flex flex-wrap items-center gap-1">
            <ControlChip
              active={quickFilter === 'issues'}
              onClick={() =>
                setQuickFilter((current) =>
                  current === 'issues' ? 'all' : 'issues',
                )
              }
            >
              Issues{totalIssues > 0 ? ` ${totalIssues}` : ''}
            </ControlChip>
            <ControlChip
              active={quickFilter === 'disabled'}
              onClick={() =>
                setQuickFilter((current) =>
                  current === 'disabled' ? 'all' : 'disabled',
                )
              }
            >
              Disabled
            </ControlChip>
            <ControlChip
              active={quickFilter === 'unrevealed'}
              onClick={() =>
                setQuickFilter((current) =>
                  current === 'unrevealed' ? 'all' : 'unrevealed',
                )
              }
            >
              Hidden
            </ControlChip>
          </div>
        )}
      </div>

      {!filtering && (
        <div className="space-y-0.5">
          <NavRow
            icon={LayoutDashboard}
            label="Overview"
            {...rowProps({ kind: 'overview' }, 1)}
            issueCount={0}
          />
          <NavRow
            icon={ListChecks}
            label="Selection preview"
            {...rowProps({ kind: 'selection' }, 1)}
          />
        </div>
      )}

      <div className="border-t border-neutral-800/60 pt-2">
        <GroupLabel>Campaign</GroupLabel>
        <div className="space-y-0.5">
          {workspace.seasons.map((season, seasonIndex) => {
            const seasonRef: QuestWorkspaceItemRef = {
              kind: 'season',
              id: season.id,
            };
            if (!isVisible(seasonRef)) {
              return null;
            }
            const seasonRow = branchProps(seasonRef, 1);
            const seasonRounds = workspace.rounds.filter(
              (round) => round.seasonId === season.id,
            );

            return (
              <Fragment key={season.id}>
                <NavRow
                  icon={CalendarRange}
                  label={seasonDisplayName(season, seasonIndex)}
                  meta={countMeta([[seasonRounds.length, 'round']])}
                  {...seasonRow}
                />
                {!seasonRow.isCollapsed &&
                  (!filtering ||
                    seasonRounds.some((round) =>
                      isVisible({ kind: 'round', id: round.id }),
                    )) && (
                    <Branch>
                      {workspace.rounds.map((round, roundIndex) => {
                        if (round.seasonId !== season.id) {
                          return null;
                        }
                        const roundRef: QuestWorkspaceItemRef = {
                          kind: 'round',
                          id: round.id,
                        };
                        if (!isVisible(roundRef)) {
                          return null;
                        }
                        const roundRow = branchProps(roundRef, 2);
                        const roundApps = workspace.apps.filter(
                          (app) => app.roundId === round.id,
                        );
                        const roundQuestCount = workspace.quests.filter(
                          (quest) =>
                            roundApps.some(
                              (app) => app.id === quest.roundAppId,
                            ),
                        ).length;

                        return (
                          <Fragment key={round.id}>
                            <NavRow
                              icon={Flag}
                              label={roundDisplayName(round, roundIndex)}
                              meta={countMeta([
                                [roundApps.length, 'app'],
                                [roundQuestCount, 'quest'],
                              ])}
                              status={round.status}
                              disabled={round.disabled}
                              {...roundRow}
                            />
                            {!roundRow.isCollapsed &&
                              (!filtering ||
                                roundApps.some((app) =>
                                  isVisible({ kind: 'app', id: app.id }),
                                )) && (
                                <Branch>
                                  {workspace.apps.map((app, appIndex) => {
                                    if (app.roundId !== round.id) {
                                      return null;
                                    }
                                    const appRef: QuestWorkspaceItemRef = {
                                      kind: 'app',
                                      id: app.id,
                                    };
                                    if (!isVisible(appRef)) {
                                      return null;
                                    }
                                    const appRow = branchProps(appRef, 3);
                                    const appQuestCount =
                                      workspace.quests.filter(
                                        (quest) => quest.roundAppId === app.id,
                                      ).length;
                                    const appDimmed =
                                      round.disabled ||
                                      app.disabled ||
                                      app.unrevealed;
                                    const appLabel = appDisplayName(
                                      app,
                                      appIndex,
                                    );
                                    const appPosition = roundApps.findIndex(
                                      (item) => item.id === app.id,
                                    );
                                    const canReorderApps =
                                      !filtering && roundApps.length > 1;
                                    const isAppDragTarget =
                                      !filtering &&
                                      draggedApp !== null &&
                                      draggedApp.roundId === round.id &&
                                      draggedApp.appId !== app.id;

                                    return (
                                      <Fragment key={app.id}>
                                        <div
                                          data-app-drag-row
                                          className="relative"
                                          onDragOver={(event) => {
                                            if (!isAppDragTarget) {
                                              return;
                                            }
                                            event.preventDefault();
                                            const dataTransfer: DataTransfer | null =
                                              event.dataTransfer;
                                            if (dataTransfer) {
                                              dataTransfer.dropEffect = 'move';
                                            }
                                            const edge = dropEdgeOf(event);
                                            setDropTarget((current) =>
                                              current?.appId === app.id &&
                                              current.edge === edge
                                                ? current
                                                : { appId: app.id, edge },
                                            );
                                          }}
                                          onDragLeave={(event) => {
                                            if (
                                              event.relatedTarget instanceof
                                                Node &&
                                              event.currentTarget.contains(
                                                event.relatedTarget,
                                              )
                                            ) {
                                              return;
                                            }
                                            setDropTarget((current) =>
                                              current?.appId === app.id
                                                ? null
                                                : current,
                                            );
                                          }}
                                          onDrop={(event) => {
                                            if (
                                              !isAppDragTarget ||
                                              draggedApp === null
                                            ) {
                                              return;
                                            }
                                            event.preventDefault();
                                            props.onMoveApp(
                                              draggedApp.appId,
                                              app.id,
                                              dropEdgeOf(event),
                                            );
                                            clearAppDrag();
                                          }}
                                        >
                                          {isAppDragTarget &&
                                            dropTarget !== null &&
                                            dropTarget.appId === app.id && (
                                              <span
                                                aria-hidden="true"
                                                className={cn(
                                                  'pointer-events-none absolute inset-x-1 z-10 h-0.5 rounded-full bg-teal-400',
                                                  dropTarget.edge === 'before'
                                                    ? '-top-px'
                                                    : '-bottom-px',
                                                )}
                                              />
                                            )}
                                          <NavRow
                                            icon={Smartphone}
                                            iconUrl={app.iconUrl || undefined}
                                            label={appLabel}
                                            meta={countMeta([
                                              [appQuestCount, 'quest'],
                                            ])}
                                            disabled={app.disabled}
                                            unrevealed={app.unrevealed}
                                            dimmed={
                                              round.disabled || app.unrevealed
                                            }
                                            {...appRow}
                                            trailing={
                                              canReorderApps ? (
                                                <button
                                                  type="button"
                                                  draggable
                                                  aria-label={`Reorder ${appLabel}`}
                                                  title="Drag to reorder within the round, or press an arrow key"
                                                  onDragStart={(event) => {
                                                    const dataTransfer: DataTransfer | null =
                                                      event.dataTransfer;
                                                    if (dataTransfer) {
                                                      dataTransfer.setData(
                                                        'text/plain',
                                                        app.id,
                                                      );
                                                      dataTransfer.effectAllowed =
                                                        'move';
                                                      const row =
                                                        event.currentTarget.closest(
                                                          '[data-app-drag-row]',
                                                        );
                                                      if (
                                                        row instanceof
                                                        HTMLElement
                                                      ) {
                                                        // Ghost the whole row,
                                                        // not just the grip.
                                                        // happy-dom throws
                                                        // "Not implemented".
                                                        try {
                                                          dataTransfer.setDragImage(
                                                            row,
                                                            12,
                                                            14,
                                                          );
                                                        } catch {
                                                          // Keep the default
                                                          // drag image.
                                                        }
                                                      }
                                                    }
                                                    setDraggedApp({
                                                      appId: app.id,
                                                      roundId: round.id,
                                                    });
                                                  }}
                                                  onDragEnd={clearAppDrag}
                                                  onKeyDown={(event) => {
                                                    if (
                                                      event.key !== 'ArrowUp' &&
                                                      event.key !== 'ArrowDown'
                                                    ) {
                                                      return;
                                                    }
                                                    event.preventDefault();
                                                    const up =
                                                      event.key === 'ArrowUp';
                                                    const sibling =
                                                      roundApps[
                                                        appPosition +
                                                          (up ? -1 : 1)
                                                      ];
                                                    if (sibling) {
                                                      props.onMoveApp(
                                                        app.id,
                                                        sibling.id,
                                                        up ? 'before' : 'after',
                                                      );
                                                    }
                                                  }}
                                                  className="flex w-5 shrink-0 cursor-grab items-center justify-center rounded text-neutral-600 transition-colors hover:text-neutral-200 active:cursor-grabbing"
                                                >
                                                  <GripVertical className="size-3.5" />
                                                </button>
                                              ) : undefined
                                            }
                                          />
                                        </div>
                                        {!appRow.isCollapsed &&
                                          (!filtering ||
                                            workspace.quests.some(
                                              (quest) =>
                                                quest.roundAppId === app.id &&
                                                isVisible({
                                                  kind: 'quest',
                                                  id: quest.id,
                                                }),
                                            )) && (
                                            <Branch>
                                              {workspace.quests.map(
                                                (quest, questIndex) =>
                                                  quest.roundAppId === app.id &&
                                                  isVisible({
                                                    kind: 'quest',
                                                    id: quest.id,
                                                  }) ? (
                                                    <NavRow
                                                      key={quest.id}
                                                      icon={Target}
                                                      label={questDisplayName(
                                                        quest,
                                                        questIndex,
                                                      )}
                                                      status={quest.status}
                                                      disabled={quest.disabled}
                                                      dimmed={appDimmed}
                                                      {...rowProps(
                                                        {
                                                          kind: 'quest',
                                                          id: quest.id,
                                                        },
                                                        4,
                                                      )}
                                                    />
                                                  ) : null,
                                              )}
                                              {!filtering && (
                                                <AddRow
                                                  label="Add quest"
                                                  onClick={() =>
                                                    props.onAddQuest(app.id)
                                                  }
                                                />
                                              )}
                                            </Branch>
                                          )}
                                      </Fragment>
                                    );
                                  })}
                                  {!filtering && (
                                    <AddRow
                                      label="Add app"
                                      onClick={() => props.onAddApp(round.id)}
                                    />
                                  )}
                                </Branch>
                              )}
                          </Fragment>
                        );
                      })}
                      {!filtering && (
                        <AddRow
                          label="Add round"
                          onClick={() => props.onAddRound(season.id)}
                        />
                      )}
                    </Branch>
                  )}
              </Fragment>
            );
          })}
          {!filtering && (
            <AddRow label="Add season" onClick={props.onAddSeason} />
          )}
        </div>
      </div>

      {filtering && visibleKeys && visibleKeys.size === 0 && (
        <p className="px-2 py-6 text-center text-xs text-neutral-500">
          Nothing matches this filter.
        </p>
      )}

      <div className="mt-1 flex items-center gap-1.5 border-t border-neutral-800/60 pt-2.5">
        <Link
          to="/quests/events"
          className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-neutral-800 px-2 py-1.5 text-xs font-medium text-neutral-300 transition-colors hover:border-neutral-700 hover:bg-neutral-800/60 hover:text-white"
        >
          <Activity className="size-3.5" />
          View events
        </Link>
        <Link
          to="/quests/claims"
          className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-neutral-800 px-2 py-1.5 text-xs font-medium text-neutral-300 transition-colors hover:border-neutral-700 hover:bg-neutral-800/60 hover:text-white"
        >
          <Coins className="size-3.5" />
          View claims
        </Link>
      </div>
    </nav>
  );
}
